import { inject, Signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
  GridColumns,
  WcgAdvancedFilter,
  WcgAdvancedFilterArrayForm,
  WcgAdvancedFilterOptions,
} from '@wcg/frontend-framework';

export function getAdvancedFilterFormGroup(): FormGroup<{
  filters: FormArray<WcgAdvancedFilterArrayForm>;
}> {
  const formBuilder = inject(FormBuilder);
  return formBuilder.group({
    filters: formBuilder.array<WcgAdvancedFilterArrayForm>([]),
  });
}

export function extractAdvancedFilters(
  advancedFilters: WcgAdvancedFilter,
  columnsCopy: Signal<GridColumns>,
) {
  if (!Object.values(advancedFilters).length) {
    return {} as WcgAdvancedFilter;
  }
  const columns = columnsCopy();
  return Object.keys(advancedFilters ?? {}).reduce(
    (acc, key: string) => {
      const filter = advancedFilters![key as keyof WcgAdvancedFilter];
      acc[key] = {
        ...filter,
        columnKey: filter?.columnKey ?? key,
        label: columns[key]?.label ?? '',
        value: filter.value ?? '',
      } as WcgAdvancedFilterOptions;
      return acc;
    },
    {} as Record<string, WcgAdvancedFilterOptions>,
  );
}

export function flattenAdvancedFilters(
  advancedFilters: WcgAdvancedFilter[],
): WcgAdvancedFilter | null {
  if (!advancedFilters?.length) {
    return null;
  }
  return advancedFilters?.reduce((acc, filter) => {
    for (const [key, value] of Object.entries(filter)) {
      acc[key] = { ...value };
    }
    return acc;
  }, {} as WcgAdvancedFilter);
}

// This function is used to reset all filters in the grid, it will be called when the user clicks on the "Reset Filters" button in the grid header. It will reset all filters to their default state and return the updated filters to the grid.
export function resetAllGridFilters(advancedFilters: WcgAdvancedFilter) {
  const _advancedFilters = advancedFilters;

  for (const [key] of Object.entries(_advancedFilters)) {
    _advancedFilters[key]['value'] = [];

    if (_advancedFilters[key]['formatted']) {
      _advancedFilters[key]['formatted'] = '';
      _advancedFilters[key]['start'] = '';
      _advancedFilters[key]['end'] = '';
    }
  }

  const filters: WcgAdvancedFilter[] = [];
  for (const [key, value] of Object.entries(_advancedFilters)) {
    filters.push({ [key]: { ...value, columnKey: value.columnKey ?? key } });
  }

  return {
    actions: 'filter',
    filters: filters,
  };
}
